/**
 * Lightweight phone helpers used by the quote forms. Strictly client-side —
 * the canonical source of truth (server-side, RLS-policy validation) is the
 * existing zod schema on each form.
 */

/** Strip everything except digits and a leading +. */
export function digitsOnly(value: string): string {
  return value.replace(/[^\d+]/g, "");
}

/**
 * Format a US-style phone number as the user types.
 *
 * Examples:
 *   "5551234"        -> "(555) 123-4"
 *   "5551234567"     -> "(555) 123-4567"
 *   "15551234567"    -> "1 (555) 123-4567"
 *   "+15551234567"   -> "+1 (555) 123-4567"
 *
 * For non-US (any input starting with + and not +1) we leave it mostly alone
 * so international visitors aren't fought by the formatter.
 */
export function formatUSPhone(input: string): string {
  if (!input) return "";
  const trimmed = input.trim();

  // International: keep + and digits, no formatting.
  if (trimmed.startsWith("+") && !trimmed.startsWith("+1")) {
    return "+" + trimmed.slice(1).replace(/[^\d]/g, "");
  }

  // US: keep optional leading 1/+1, then group as (xxx) xxx-xxxx.
  let raw = digitsOnly(trimmed);
  let prefix = "";
  if (raw.startsWith("+1")) {
    prefix = "+1 ";
    raw = raw.slice(2);
  } else if (raw.startsWith("1") && raw.length > 10) {
    prefix = "1 ";
    raw = raw.slice(1);
  }
  raw = raw.slice(0, 10);

  if (raw.length === 0) return prefix.trim();
  if (raw.length < 4) return `${prefix}(${raw}`;
  if (raw.length < 7) return `${prefix}(${raw.slice(0, 3)}) ${raw.slice(3)}`;
  return `${prefix}(${raw.slice(0, 3)}) ${raw.slice(3, 6)}-${raw.slice(6)}`;
}

/** Cheap "looks valid enough to submit" check for inline validation. */
export function isLikelyPhone(input: string): boolean {
  const d = digitsOnly(input).replace(/^\+?1/, "");
  return d.length >= 7 && d.length <= 15;
}
